<template>
    <div class="report-page">
        <header class="report-header">
            <div class="report-back">
                <button type="button" class="back-btn" @click="goBack">
                    <ChatIcon name="arrow-left" :size="14" />
                    <span>Все отчёты</span>
                </button>
            </div>

            <div v-if="loading" class="report-skeleton">
                <div class="skeleton-line skeleton-title" />
                <div class="skeleton-line skeleton-subtitle" />
            </div>

            <div v-else-if="error" class="report-error">
                <h1>Отчёт не найден</h1>
                <p>Возможно, он был снят с публикации или ссылка устарела.</p>
                <button type="button" class="primary-btn" @click="goBack">
                    Вернуться в чат
                </button>
            </div>

            <div v-else class="report-meta">
                <div class="report-meta-top">
                    <span v-if="report.category" class="badge">{{ categoryLabel }}</span>
                    <span class="meta-date">Обновлён {{ formattedDate }}</span>
                    <span class="meta-views">
                        <ChatIcon name="eye" :size="12" />
                        {{ report.viewCount || 0 }}
                    </span>
                </div>
                <h1 class="report-title">{{ report.title }}</h1>
                <p v-if="report.description" class="report-description">
                    {{ report.description }}
                </p>

                <div class="report-actions">
                    <button
                        v-if="isAuthenticated"
                        type="button"
                        class="primary-btn"
                        :disabled="forking"
                        @click="onFork"
                    >
                        <ChatIcon name="copy" :size="14" />
                        {{ forking ? 'Открываю…' : 'Открыть в моём чате' }}
                    </button>
                    <button
                        v-else
                        type="button"
                        class="primary-btn"
                        @click="goToLogin"
                    >
                        <ChatIcon name="copy" :size="14" />
                        Войти, чтобы продолжить
                    </button>
                </div>
            </div>
        </header>

        <main v-if="!error" class="report-body">
            <ChatPreloader
                v-if="loading"
                label="Загружаем отчёт…"
            />
            <template v-else>
            <div v-if="messages.length === 0" class="empty-state">
                В этом отчёте пока нет сообщений.
            </div>
            <div v-else class="messages-list">
                <ChatMessage
                    v-for="m in messages"
                    :key="m.id"
                    :message="m"
                    report-view
                    :user-name="m.role === 'user' ? 'Аналитик' : ''"
                    :user-initials="m.role === 'user' ? 'АН' : 'AM'"
                    @followup="onFollowup"
                />
            </div>
            </template>
        </main>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore as useVuexStore } from 'vuex'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import ChatMessage from '@/components/chat/conversation/ChatMessage.vue'
import ChatPreloader from '@/components/chat/ChatPreloader.vue'
import { getPublicReport, forkPublicReport } from '@/utils/chatApi'
import { rebuildMessageFromServer } from '@/components/chat/state/useChatStore'

const route = useRoute()
const router = useRouter()
const vuexStore = useVuexStore()

const loading = ref(true)
const error = ref(false)
const forking = ref(false)
const followupLoading = ref(false)
const report = ref({})
const messages = ref([])

const isAuthenticated = computed(() => {
    const info = vuexStore.getters['main/userInfo']
    return info && Object.keys(info).length > 0
})

const CATEGORY_LABELS = {
    market: 'Рынок',
    forecast: 'Прогноз',
    'top-brands': 'Топ-марки',
    brands: 'Топ-марки',
    regions: 'Регионы',
    credit: 'Кредиты',
    loans: 'Кредиты',
    commercial: 'Коммерческий',
    ev: 'EV',
    used: 'С пробегом',
    dealers: 'Дилеры',
}

const categoryLabel = computed(() => {
    const c = (report.value?.category || '').toLowerCase()
    return CATEGORY_LABELS[c] || (report.value?.category || '')
})

const formattedDate = computed(() => {
    const raw = report.value?.updatedAt
    if (!raw) return ''
    try {
        return new Date(raw).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        })
    } catch (e) {
        return ''
    }
})

async function loadReport() {
    const token = route.params.token
    if (!token) {
        error.value = true
        loading.value = false
        return
    }
    loading.value = true
    error.value = false
    try {
        const data = await getPublicReport(token)
        report.value = data
        messages.value = (data.messages || []).map(rebuildMessageFromServer)
    } catch (e) {
        error.value = true
        report.value = {}
        messages.value = []
    } finally {
        loading.value = false
    }
}

async function onFork() {
    const token = route.params.token
    if (!token || forking.value) return
    forking.value = true
    try {
        const threadId = await forkPublicReport(token)
        if (threadId) {
            router.push({ name: 'Chat', query: { thread: threadId } })
        }
    } catch (e) {
        forking.value = false
    }
}

function goBack() {
    router.push({ name: 'Chat' })
}

function goToLogin() {
    router.push({ name: 'Login', query: { redirect: route.fullPath } })
}

async function onFollowup(action) {
    const label = (action?.label || action?.prompt || '').trim()
    if (!label) return

    if (!isAuthenticated.value) {
        router.push({ name: 'Login', query: { redirect: route.fullPath } })
        return
    }

    const token = route.params.token
    if (!token || followupLoading.value || forking.value) return

    followupLoading.value = true
    try {
        const threadId = await forkPublicReport(token)
        if (threadId) {
            router.push({
                name: 'Chat',
                query: { thread: threadId, prompt: label },
            })
        }
    } catch (e) {
        // fork недоступен — пользователь остаётся на отчёте
    } finally {
        followupLoading.value = false
    }
}

onMounted(loadReport)

watch(() => route.params.token, loadReport)
</script>

<style scoped>
.report-page {
    min-height: 100vh;
    background: white;
    padding: 0 0 64px;
}

.report-header {
    max-width: 880px;
    margin: 0 auto;
    padding: 24px 24px 12px;
}

.report-back {
    margin-bottom: 16px;
}

.back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 0;
    font-family: inherit;
    font-size: 13px;
    color: #5F5E5A;
    cursor: pointer;
    padding: 4px 6px;
    margin-left: -6px;
    border-radius: 6px;
}

.back-btn:hover {
    color: #23282C;
    background: #F4F2EC;
}

.report-skeleton {
    padding: 24px 0;
}

.skeleton-line {
    background: #F4F2EC;
    border-radius: 6px;
    margin-bottom: 12px;
    animation: skel 1.2s ease-in-out infinite;
}

.skeleton-title { height: 28px; width: 70%; }
.skeleton-subtitle { height: 16px; width: 40%; }

@keyframes skel {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
}

.report-error {
    padding: 60px 0;
    text-align: center;
    color: #5F5E5A;
}

.report-error h1 {
    font-size: 20px;
    margin: 0 0 8px;
    color: #23282C;
}

.report-error p {
    margin: 0 0 24px;
    font-size: 14px;
}

.report-meta-top {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    font-size: 12px;
    color: #888780;
    flex-wrap: wrap;
}

.badge {
    background: #F1EFE8;
    color: #23282C;
    padding: 3px 8px;
    border-radius: 4px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-size: 10.5px;
}

.meta-date {
    font-variant-numeric: tabular-nums;
}

.meta-views {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.report-title {
    font-size: 26px;
    font-weight: 700;
    color: #23282C;
    margin: 0 0 8px;
    line-height: 1.25;
}

.report-description {
    margin: 0 0 18px;
    font-size: 15px;
    color: #5F5E5A;
    line-height: 1.55;
}

.report-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.primary-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #E30613;
    color: white;
    border: 0;
    padding: 9px 14px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    font-family: inherit;
}

.primary-btn:hover { background: #C0050F; }
.primary-btn:disabled { opacity: 0.6; cursor: progress; }

.report-body {
    max-width: 880px;
    margin: 0 auto;
    padding: 12px 24px;
    border-top: 1px solid #F0EEE8;
}

.empty-state {
    padding: 40px 0;
    text-align: center;
    color: #888780;
    font-size: 13px;
}

.messages-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

@media (max-width: 720px) {
    .report-header { padding: 16px 16px 8px; }
    .report-body { padding: 12px 16px; }
    .report-title { font-size: 22px; }
}
</style>
